class CartNotification extends HTMLElement {
  constructor() {
    super();
    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }
  open() {
    this.notification.classList.add('animate', 'active');
    this.notification.addEventListener('transitionend', () => {
      this.notification.focus();
      trapFocus(this.notification);
    }, { once: true });
    document.body.addEventListener('click', this.onBodyClick);
    this.dispatchCartViewEvent();
  }
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;
    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;
      this.dispatchEvent(new CartViewEvent({
        context: 'dialog',
        cart: CartViewEvent.createCartFromAjaxResponse(cart),
      }));
    } catch (e) {}
  }
  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);
    removeTrapFocus(this.activeElement);
  }
  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });
    if (this.header) this.header.reveal();
    this.open();
  }
  getSectionsToRender() {
    return [
      { id: 'cart-notification-product', selector: `[id="cart-notification-product-${this.cartItemKey}"]` },
      { id: 'cart-notification-button' },
      { id: 'cart-icon-bubble' },
    ];
  }
  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }
  setActiveElement(element) { this.activeElement = element; }
}
customElements.define('cart-notification', CartNotification);

(() => {
  if (window.__doeMiniCartV4) return;
  window.__doeMiniCartV4 = true;

  const ADD_URL = /\/cart\/add(?:\.js)?(?:\?|$)/;
  const nativeFetch = window.fetch.bind(window);
  let activeButton = null;
  let preparingTimer = null;
  let preparingExpiryTimer = null;
  let requestTimer = null;
  let restoreTimer = null;
  let cartRefreshPromise = null;
  let pendingAdds = 0;
  let preparing = false;
  const ownedAria = new WeakMap();

  const cartUrl = () => window.routes?.cart_url || '/cart';

  const getCart = async () => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await nativeFetch(cartUrl() + '.js', {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        cache: 'no-store',
        signal: controller.signal
      });
      if (!response.ok) throw new Error('Unable to read cart');
      return await response.json();
    } finally {
      clearTimeout(timeout);
    }
  };

  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    })[character]);

  const money = (cents, currency) => {
    try {
      return new Intl.NumberFormat('zh-TW', {
        style: 'currency',
        currency: currency || 'TWD',
        maximumFractionDigits: 0
      }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return 'NT$' + Math.round((Number(cents) || 0) / 100);
    }
  };

  const buttonLabel = (button) =>
    button?.querySelector('span:not(.loading__spinner):not(.svg-wrapper)') || button;

  const isVisible = (button) =>
    button?.isConnected && button.getClientRects().length > 0 &&
    getComputedStyle(button).visibility !== 'hidden';

  const isAddButton = (button) =>
    button && !button.matches('#customily-preview-button, .doe-cus-preview') &&
    (button.matches('[name="add"], #customily-cart-btn, .doe-cus-add, [data-add-to-cart]') ||
      /加入購物車|add\s*to\s*cart/i.test(button.value || button.textContent || ''));

  const rememberButton = (button) => {
    if (!button) return;
    const scope = button.closest('product-info, .product, main') || document;
    const customilyButton = Array.from(scope.querySelectorAll('#customily-cart-btn, .doe-cus-add'))
      .find(isVisible);
    const target = customilyButton || (isVisible(button) ? button : null);
    if (!target) return;
    activeButton = target;
    const current = target.tagName === 'INPUT' ? target.value : buttonLabel(target)?.textContent;
    if (!target.dataset.doeLabel || /正在|已加入|加入失敗/.test(target.dataset.doeLabel)) {
      target.dataset.doeLabel = /正在|已加入|加入失敗/.test(current || '')
        ? (window.variantStrings?.addToCart || '加入購物車')
        : (current || window.variantStrings?.addToCart || '加入購物車').trim();
    }
  };

  const applyButtonText = (button, text) => {
    if (!button) return;
    if (button.tagName === 'INPUT') {
      button.value = text;
      return;
    }
    const label = buttonLabel(button);
    if (label) label.textContent = text;
  };

  const setButton = (state) => {
    const candidate = activeButton?.isConnected ? activeButton : document.querySelector(
      '#customily-cart-btn, .doe-cus-add, product-form button[name="add"]'
    );
    rememberButton(candidate);
    const button = activeButton;
    if (!isVisible(button)) return;
    clearTimeout(restoreTimer);
    const loading = state === 'loading';
    button.classList.toggle('loading', loading);
    if (loading) {
      if (!ownedAria.has(button)) ownedAria.set(button, {
        busy: button.getAttribute('aria-busy'),
        disabled: button.getAttribute('aria-disabled')
      });
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
    } else if (ownedAria.has(button)) {
      const original = ownedAria.get(button);
      for (const [name, value] of [['aria-busy', original.busy], ['aria-disabled', original.disabled]]) {
        if (value === null) button.removeAttribute(name);
        else button.setAttribute(name, value);
      }
      ownedAria.delete(button);
      button.querySelectorAll('.loading__spinner, [class*="spinner"]').forEach((spinner) => {
        spinner.classList.add('hidden');
      });
    }

    if (state === 'preparing') applyButtonText(button, '正在準備客製資料…');
    if (state === 'loading') applyButtonText(button, '正在加入…');
    if (state === 'success') applyButtonText(button, '已加入購物車 ✓');
    if (state === 'error') applyButtonText(button, '加入失敗，請重試');
    if (state === 'idle') applyButtonText(button, button.dataset.doeLabel || '加入購物車');

    if (state === 'success' || state === 'error') {
      restoreTimer = setTimeout(() => {
        if (!pendingAdds && !preparing && !cartRefreshPromise) setButton('idle');
      }, state === 'success' ? 1600 : 2800);
    }
  };

  const toast = (message, error = false) => {
    let element = document.getElementById('doe-cart-feedback');
    if (!element) {
      element = document.createElement('div');
      element.id = 'doe-cart-feedback';
      element.setAttribute('role', 'status');
      element.setAttribute('aria-live', 'polite');
      Object.assign(element.style, {
        position: 'fixed',
        right: '20px',
        top: '88px',
        zIndex: '1000001',
        maxWidth: '340px',
        padding: '14px 18px',
        borderRadius: '10px',
        color: '#fff',
        fontSize: '15px',
        lineHeight: '1.45',
        boxShadow: '0 8px 30px rgba(0,0,0,.18)',
        transition: 'opacity .2s'
      });
      document.body.appendChild(element);
    }
    element.style.background = error ? '#9f2d2d' : '#2d6a4f';
    element.textContent = message;
    element.style.opacity = '1';
    clearTimeout(element._hideTimer);
    element._hideTimer = setTimeout(() => {
      element.style.opacity = '0';
    }, 3200);
  };

  const closeDrawer = () => {
    const root = document.getElementById('doe-mini-cart');
    if (root) {
      root.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      const panel = root.querySelector('.doe-mini-cart__panel');
      if (panel && typeof removeTrapFocus === 'function') removeTrapFocus(activeButton);
      if (activeButton?.isConnected) activeButton.focus({ preventScroll: true });
    }
    resetButton();
  };

  const ensureDrawer = () => {
    let root = document.getElementById('doe-mini-cart');
    if (root) return root;

    root = document.createElement('div');
    root.id = 'doe-mini-cart';
    root.innerHTML = `
      <div class="doe-mini-cart__overlay" data-doe-close></div>
      <aside class="doe-mini-cart__panel" role="dialog" aria-modal="true" aria-label="購物車" tabindex="-1">
        <div class="doe-mini-cart__header">
          <strong>已加入購物車 ✓</strong>
          <button type="button" data-doe-close aria-label="關閉購物車">×</button>
        </div>
        <div class="doe-mini-cart__items"></div>
        <div class="doe-mini-cart__footer">
          <div class="doe-mini-cart__status" role="status" aria-live="polite"></div>
          <div class="doe-mini-cart__total"></div>
          <a class="button button--secondary" href="${cartUrl()}">查看購物車</a>
          <a class="button" href="/checkout">前往結帳</a>
        </div>
      </aside>`;

    const style = document.createElement('style');
    style.textContent = `
      #doe-mini-cart{position:fixed;inset:0;z-index:1000000;pointer-events:none;visibility:hidden}
      #doe-mini-cart.is-open{pointer-events:auto;visibility:visible}
      .doe-mini-cart__overlay{position:absolute;inset:0;background:rgba(0,0,0,.38);opacity:0;transition:opacity .25s}
      .is-open .doe-mini-cart__overlay{opacity:1}
      .doe-mini-cart__panel{position:absolute;right:0;top:0;height:100%;width:min(420px,92vw);background:#fff;color:#222;transform:translateX(100%);transition:transform .28s ease;display:flex;flex-direction:column;box-shadow:-10px 0 35px rgba(0,0,0,.18)}
      .is-open .doe-mini-cart__panel{transform:translateX(0)}
      .doe-mini-cart__header{display:flex;justify-content:space-between;align-items:center;padding:22px;border-bottom:1px solid #e5e5e5;font-size:18px}
      .doe-mini-cart__header button{border:0;background:transparent;font-size:30px;line-height:1;cursor:pointer}
      .doe-mini-cart__items{padding:10px 22px;overflow:auto;flex:1}
      .doe-mini-cart__item{display:grid;grid-template-columns:72px 1fr auto;gap:14px;padding:14px 0;border-bottom:1px solid #eee;align-items:center}
      .doe-mini-cart__item img{width:72px;height:72px;object-fit:cover;border-radius:6px;background:#f4f4f4}
      .doe-mini-cart__item-title{font-weight:600;line-height:1.35}
      .doe-mini-cart__item-meta{font-size:13px;color:#777;margin-top:5px}
      .doe-mini-cart__footer{padding:18px 22px 24px;border-top:1px solid #e5e5e5;display:grid;gap:10px}
      .doe-mini-cart__total{display:flex;justify-content:space-between;font-weight:700;font-size:17px;margin-bottom:4px}
      .doe-mini-cart__footer .button{text-align:center;text-decoration:none;width:100%}
      .doe-mini-cart__remove{min-height:44px;min-width:44px;padding:8px 0;border:0;background:transparent;color:#735338;text-decoration:underline;font:inherit;cursor:pointer}
      .doe-mini-cart__remove:disabled{opacity:.5;cursor:wait}
      .doe-mini-cart__remove:focus-visible{outline:2px solid #735338;outline-offset:2px}
      .doe-mini-cart__item>div{min-width:0;overflow-wrap:anywhere}
      .doe-mini-cart__footer a[hidden]{display:none}
      .doe-mini-cart__status{font-size:14px;color:#8c301f}
    `;
    document.head.appendChild(style);
    document.body.appendChild(root);

    root.querySelectorAll('[data-doe-close]').forEach((element) => {
      element.addEventListener('click', closeDrawer);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && root.classList.contains('is-open')) closeDrawer();
    });
    root.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-doe-remove]');
      if (!button || root.dataset.removing === 'true') return;
      const key = button.dataset.doeRemove;
      const index = Array.from(root.querySelectorAll('[data-doe-remove]')).indexOf(button);
      root.dataset.removing = 'true';
      const panel = root.querySelector('.doe-mini-cart__panel');
      panel.setAttribute('aria-busy', 'true');
      root.querySelectorAll('[data-doe-remove]').forEach((item) => {
        item.disabled = true;
      });
      const status = root.querySelector('.doe-mini-cart__status');
      status.textContent = '正在移除…';

      try {
        const response = await nativeFetch(cartUrl() + '/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
        if (!response.ok) throw new Error('Remove failed');
        const cart = await response.json();
        if (!Array.isArray(cart.items) || cart.items.some((item) => item.key === key)) {
          throw new Error('Remove failed');
        }
        updateBadge(cart.item_count);
        openDrawer(cart, true);
        const buttons = root.querySelectorAll('[data-doe-remove]');
        (buttons[Math.min(index, buttons.length - 1)] ||
          root.querySelector('button[data-doe-close]'))?.focus();
        root.querySelector('.doe-mini-cart__status').textContent = '商品已移除';
      } catch (_) {
        status.textContent = '無法移除商品，請稍後再試。';
      } finally {
        root.dataset.removing = 'false';
        panel.removeAttribute('aria-busy');
        root.querySelectorAll('[data-doe-remove]').forEach((item) => {
          item.disabled = false;
        });
      }
    });

    return root;
  };

  const updateBadge = (count) => {
    const link =
      document.getElementById('cart-icon-bubble') || document.querySelector('a[href*="/cart"]');
    if (!link) return;
    let bubble = link.querySelector('.cart-count-bubble');
    if (count <= 0) {
      bubble?.remove();
      link.setAttribute('aria-label', '購物車，0 件商品');
      return;
    }
    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      bubble.innerHTML =
        '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
      link.appendChild(bubble);
    }
    const visible =
      bubble.querySelector('span[aria-hidden="true"]') || bubble.querySelector('span');
    const hidden = bubble.querySelector('.visually-hidden');
    if (visible) visible.textContent = count < 100 ? String(count) : '';
    if (hidden) hidden.textContent = '購物車內有 ' + count + ' 件商品';
    link.setAttribute('aria-label', '購物車，' + count + ' 件商品');
  };

  const openDrawer = (cart, updating = false) => {
    const root = ensureDrawer();
    const items = root.querySelector('.doe-mini-cart__items');
    items.innerHTML = cart.items
      .map(
        (item) => `
          <div class="doe-mini-cart__item">
            ${item.image ? '<img src="' + escapeHtml(item.image) + '" alt="">' : '<div></div>'}
            <div>
              <div class="doe-mini-cart__item-title">${escapeHtml(
                item.product_title || item.title
              )}</div>
              <div class="doe-mini-cart__item-meta">數量：${item.quantity}</div>
              <button type="button" class="doe-mini-cart__remove" data-doe-remove="${escapeHtml(
                item.key
              )}" aria-label="移除 ${escapeHtml(item.product_title || item.title)}">移除</button>
            </div>
            <div>${money(item.final_line_price, cart.currency)}</div>
          </div>
        `
      )
      .join('');
    if (!cart.items.length) items.innerHTML = '<p>購物車目前是空的</p>';

    root.querySelector('.doe-mini-cart__total').innerHTML =
      '<span>小計（' +
      cart.item_count +
      ' 件）</span><span>' +
      money(cart.total_price, cart.currency) +
      '</span>';
    root.querySelector('.doe-mini-cart__header strong').textContent = updating
      ? '購物車'
      : '已加入購物車 ✓';
    root.querySelector('a[href="/checkout"]').hidden = cart.item_count === 0;
    if (!updating) root.querySelector('.doe-mini-cart__status').textContent = '';

    document.querySelector('cart-notification')?.close?.();
    root.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    const panel = root.querySelector('.doe-mini-cart__panel');
    panel.focus({ preventScroll: true });
    if (typeof trapFocus === 'function') trapFocus(panel, panel);
  };

  const clearPreparation = () => {
    clearTimeout(preparingTimer);
    clearTimeout(preparingExpiryTimer);
    preparingTimer = null;
    preparingExpiryTimer = null;
  };

  const resetButton = () => {
    if (pendingAdds || cartRefreshPromise) return;
    clearPreparation();
    preparing = false;
    setButton('idle');
  };

  const beginPreparation = (button) => {
    rememberButton(button);
    clearPreparation();
    preparing = true;
    preparingTimer = setTimeout(() => {
      if (preparing) setButton('preparing');
    }, 180);
    preparingExpiryTimer = setTimeout(() => {
      clearPreparation();
      preparing = false;
      setButton('idle');
      toast('客製資料準備時間較長，請確認預覽完成後再試。', true);
    }, 20000);
  };

  const beginAdd = () => {
    pendingAdds += 1;
    preparing = false;
    clearPreparation();
    clearTimeout(requestTimer);
    setButton('loading');
    requestTimer = setTimeout(() => {
      requestTimer = null;
      setButton('error');
      toast('加入購物車等待時間過長，請檢查網路後再試。', true);
    }, 18000);
  };

  const finishAdd = (ok) => {
    pendingAdds = Math.max(0, pendingAdds - 1);
    preparing = false;
    clearPreparation();
    clearTimeout(requestTimer);
    requestTimer = null;
    if (!ok) {
      setButton('error');
      toast('加入失敗，請確認已完成照片上傳與預覽後再試。', true);
      return;
    }

    if (!cartRefreshPromise) {
      cartRefreshPromise = getCart()
        .then((cart) => {
          updateBadge(cart.item_count);
          openDrawer(cart);
          setButton('success');
        })
        .catch(() => {
          setButton('success');
          toast('商品已加入，請點右上角購物車查看');
        })
        .finally(() => {
          cartRefreshPromise = null;
        });
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.('button, input[type="submit"], input[type="button"], [role="button"]');
      if (!event.isTrusted || event.defaultPrevented || !isVisible(button) || !isAddButton(button)) return;
      if (button.disabled || pendingAdds || preparing || cartRefreshPromise) return;
      beginPreparation(button);
    },
    true
  );

  const validationFailed = () => {
    if (preparing && !pendingAdds) resetButton();
  };
  window.addEventListener('customily-required-error', validationFailed);
  window.addEventListener('customily-add-addons-error', validationFailed);
  document.addEventListener('invalid', (event) => {
    if (activeButton?.closest('product-info, .product, main')?.contains(event.target)) validationFailed();
  }, true);

  window.fetch = async function (input, init) {
    const url = typeof input === 'string' ? input : input?.url || '';
    const isAdd = ADD_URL.test(url);
    if (isAdd) beginAdd();

    try {
      const response = await nativeFetch(input, init);
      if (isAdd) {
        let ok = response.ok;
        try {
          const data = await response.clone().json();
          if (data?.status || data?.errors) ok = false;
        } catch (_) {}
        finishAdd(ok);
      }
      return response;
    } catch (error) {
      if (isAdd) finishAdd(false);
      throw error;
    }
  };

  const nativeOpen = XMLHttpRequest.prototype.open;
  const nativeSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this._doeCartAdd = ADD_URL.test(String(url));
    return nativeOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function (...args) {
    if (!this._doeCartAdd) return nativeSend.apply(this, args);

    beginAdd();
    let completed = false;
    const finish = (ok) => {
      if (completed) return;
      completed = true;
      if (ok) {
        try {
          const data =
            this.responseType === 'json' ? this.response : JSON.parse(this.responseText);
          if (data?.status || data?.errors) ok = false;
        } catch (_) {}
      }
      finishAdd(ok);
    };

    this.addEventListener('load', () => finish(this.status >= 200 && this.status < 300), {
      once: true
    });
    this.addEventListener('error', () => finish(false), { once: true });
    this.addEventListener('abort', () => finish(false), { once: true });
    this.addEventListener('timeout', () => finish(false), { once: true });
    return nativeSend.apply(this, args);
  };

  window.addEventListener('pageshow', resetButton);
})();
